import { useLocation, useNavigate, useSearchParams } from "react-router";

const TASK_PARAM = "task";

interface TaskLocationState {
  taskOpenedInApp?: boolean;
}

function openedInApp(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as TaskLocationState).taskOpenedInApp === true
  );
}

/**
 * The open task lives in the URL (?task=NOON-12), so a task can be shared as a link and Back
 * closes it. Closing a task opened in the app steps back in history; closing one that arrived
 * by link only drops the parameter, so it never leaves the app.
 */
export function useTaskParam() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const openTask = (reference: string) => {
    const next = new URLSearchParams(params);
    next.set(TASK_PARAM, reference);
    setParams(next, { state: { taskOpenedInApp: true } satisfies TaskLocationState });
  };

  /** Show another reference in the open panel, as when a task moves and is renumbered. */
  const replaceTask = (reference: string) => {
    const next = new URLSearchParams(params);
    next.set(TASK_PARAM, reference);
    setParams(next, { replace: true, state: location.state as unknown });
  };

  const closeTask = () => {
    if (openedInApp(location.state)) {
      void navigate(-1);
      return;
    }
    const next = new URLSearchParams(params);
    next.delete(TASK_PARAM);
    setParams(next, { replace: true });
  };

  return { reference: params.get(TASK_PARAM), openTask, replaceTask, closeTask };
}

/**
 * Link target that opens a task over the current page. Pass the page's own search parameters
 * (such as a list's filters) to keep them.
 */
export function taskLink(reference: string, keep: URLSearchParams = new URLSearchParams()) {
  const search = new URLSearchParams(keep);
  search.set(TASK_PARAM, reference);
  return {
    to: { search: `?${search.toString()}` },
    state: { taskOpenedInApp: true } satisfies TaskLocationState,
  };
}
